/**
 * Leave Types Create Page — hardcoded form for creating a leave type.
 */

import * as React from 'react';
import { useNavigate } from 'react-router';
import { useCreate } from '@refinedev/core';
import {
  Button, Card, CardContent, CardHeader, CardTitle,
  Input, Label, Textarea, Checkbox,
} from '@yesbos/ui';
import { ArrowLeft } from 'lucide-react';

export function LeaveTypesCreatePage() {
  const navigate = useNavigate();
  const { mutate: create, isLoading } = useCreate();

  const [form, setForm] = React.useState({
    name: '',
    code: '',
    annual_quota: 0,
    is_paid: true,
    is_carry_forward: false,
    max_carry_forward: 0,
    is_encashable: false,
    applicable_gender: '',
    min_service_days: 0,
    max_consecutive_days: '',
    description: '',
    is_active: true,
  });

  function set(field: string, value: unknown) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    create(
      { resource: 'leave_types', values: form },
      { onSuccess: () => navigate('/leave-types') },
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/leave-types')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-semibold">Create Leave Type</h1>
      </div>

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="name">Name *</Label>
                <Input id="name" value={form.name} onChange={e => set('name', e.target.value)} required />
              </div>
              <div className="space-y-1">
                <Label htmlFor="code">Code *</Label>
                <Input id="code" value={form.code} onChange={e => set('code', e.target.value.toUpperCase())} required placeholder="e.g. CL, EL, SL" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="annual_quota">Annual Quota (days)</Label>
                <Input id="annual_quota" type="number" min={0} value={form.annual_quota} onChange={e => set('annual_quota', parseInt(e.target.value) || 0)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="max_consecutive_days">Max Consecutive Days</Label>
                <Input id="max_consecutive_days" type="number" min={0} value={form.max_consecutive_days} onChange={e => set('max_consecutive_days', e.target.value)} placeholder="Unlimited" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="min_service_days">Min Service Days</Label>
                <Input id="min_service_days" type="number" min={0} value={form.min_service_days} onChange={e => set('min_service_days', parseInt(e.target.value) || 0)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="applicable_gender">Applicable Gender</Label>
                <select
                  id="applicable_gender"
                  className="w-full border border-input bg-background rounded-md px-3 py-2 text-sm"
                  value={form.applicable_gender}
                  onChange={e => set('applicable_gender', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="max_carry_forward">Max Carry Forward Days</Label>
                <Input id="max_carry_forward" type="number" min={0} value={form.max_carry_forward} onChange={e => set('max_carry_forward', parseInt(e.target.value) || 0)} />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" value={form.description} onChange={e => set('description', e.target.value)} rows={3} />
            </div>

            <div className="flex flex-wrap gap-6 pt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={form.is_paid} onCheckedChange={v => set('is_paid', v)} />
                <span className="text-sm">Paid Leave</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={form.is_carry_forward} onCheckedChange={v => set('is_carry_forward', v)} />
                <span className="text-sm">Carry Forward</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={form.is_encashable} onCheckedChange={v => set('is_encashable', v)} />
                <span className="text-sm">Encashable</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={form.is_active} onCheckedChange={v => set('is_active', v)} />
                <span className="text-sm">Active</span>
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => navigate('/leave-types')}>Cancel</Button>
              <Button type="submit" disabled={isLoading}>{isLoading ? 'Creating...' : 'Create Leave Type'}</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default LeaveTypesCreatePage;
